import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { z } from 'zod';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { getCssVar } from '@norkron/web-themer';
import { withConformance, registerResponsiveContract } from '@norkron/web-conformance';
import { createContext, memo, useMemo, useCallback, useRef, useState, useId, useEffect, useContext, isValidElement, cloneElement } from 'react';
import { Drawer, DrawerContent, DrawerBody } from '@norkron/web-drawer';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';
import { useFocusRestore, useDisclosure } from '@norkron/web-a11y';
import { RichTooltip } from '@norkron/web-tooltip';
import { EmptyState } from '@norkron/web-states';
import { jsonStorage } from '@norkron/web-storage';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

// src/error-codes.ts
var WEB_SIDEBAR_LIB = "@norkron/web-sidebar";
var WEB_SIDEBAR_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-sidebar");
var { warn, panic, reportInvalidInput } = createScopedErrors(WEB_SIDEBAR_LIB, WEB_SIDEBAR_TOKEN);
try {
  registerCodes(WEB_SIDEBAR_LIB, {
    WEB_SIDEBAR_INVALID_INPUT: {
      description: "A public input to @norkron/web-sidebar failed zod validation (Wave 10 mandate). Payload follows WEB_INVALID_INPUT_SHAPE: { fieldPath, expected, received }.",
      severity: "warning",
      docsAnchor: "web-sidebar/invalid-input",
      exampleFix: "Match the per-export schema (SidebarPropsSchema, SidebarLayoutPropsSchema, UseSidebarOptionsSchema, etc.)."
    },
    WEB_SIDEBAR_INVALID_WIDTH: {
      description: "A width value (persisted in localStorage or programmatically supplied) fell outside [SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH] and was clamped. Persisted payloads that fail JSON.parse are reported with this code too.",
      severity: "warning",
      docsAnchor: "web-sidebar/invalid-width",
      exampleFix: "Pass a numeric width within [200, 600]. If the value came from localStorage, clear the key or migrate the persisted shape."
    },
    SB_NO_SKIP_LINK: {
      description: "selfCheck observed a sidebar surface without an associated skip-link affordance enabling keyboard users to bypass the navigation block.",
      severity: "warning",
      docsAnchor: "web-sidebar/self-check#no-skip-link"
    },
    SB_STATE_CLASS_INERT: {
      description: "selfCheck observed a @norkron/web-sidebar state class (e.g. sidebar__item--active) in the DOM with no matching rule in any loaded stylesheet. The state renders identically to the default state, so sighted users lose the affordance \u2014 for `--active`, that means the current page is not indicated.",
      severity: "warning",
      docsAnchor: "web-sidebar/self-check#state-class-inert",
      exampleFix: "Add `import '@norkron/web-sidebar/styles.css'` once at the app entry, or author rules for the reported classes in your own stylesheet."
    },
    SB_TOKEN_UNDEFINED: {
      description: "selfCheck observed that a --sidebar__* theme token the shipped stylesheet paints through is undefined in the live cascade, so the rule falls back to a hardcoded literal that ignores the active theme. This is the drift shape that produced near-white-on-near-white hover text in a live client app.",
      severity: "warning",
      docsAnchor: "web-sidebar/self-check#token-undefined",
      exampleFix: `Ship @norkron/web-themer's base.css unmodified \u2014 it declares every --sidebar__* token in both the light :root and the :root[data-theme="dark"] block.`
    },
    SB_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck threw internally while inspecting a sidebar instance; reported as a finding with severity info.",
      severity: "info",
      docsAnchor: "web-sidebar/self-check#internal-error"
    },
    SB_SELFCHECK_BUDGET_EXCEEDED: {
      description: "selfCheck exceeded its per-call time budget while walking sidebar instances; partial results returned.",
      severity: "info",
      docsAnchor: "web-sidebar/self-check#budget-exceeded"
    }
  });
} catch {
}

// src/constants.ts
var SIDEBAR_MIN_WIDTH = 200;
var SIDEBAR_MAX_WIDTH = 600;
var SIDEBAR_DEFAULT_WIDTH = 240;
var SIDEBAR_COLLAPSED_WIDTH = 48;
var SIDEBAR_KEYBOARD_STEP = 10;

// src/__schemas__/Sidebar.schema.ts
var SidebarPropsSchema = z.object({
  children: z.unknown().optional(),
  className: z.string().optional(),
  ariaLabel: z.string().optional(),
  side: z.enum(["start", "end"]).optional(),
  resizable: z.boolean().optional(),
  collapsible: z.union([z.boolean(), z.enum(["none", "icon", "full"])]).optional(),
  testId: z.string().optional(),
  defaultWidth: z.number().int().min(SIDEBAR_MIN_WIDTH).max(SIDEBAR_MAX_WIDTH).optional(),
  defaultCollapsed: z.boolean().optional(),
  width: z.number().int().min(SIDEBAR_MIN_WIDTH).max(SIDEBAR_MAX_WIDTH).optional(),
  collapsed: z.boolean().optional(),
  onCollapsedChange: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function"
  }).optional(),
  onWidthChange: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function"
  }).optional(),
  storageKey: z.string().min(1).optional(),
  persistKey: z.string().min(1).optional(),
  hotkey: z.string().min(1).optional(),
  // ⚠️ `nonnegative`, not `positive`. `SidebarProps.mobileBreakpoint` documents
  // "Set to `0` to disable the mobile drawer mode" and `useIsMobile` honours
  // that, but `positive()` REJECTED the documented value — so the one consumer
  // who followed the doc got a `WEB_SIDEBAR_INVALID_INPUT` warning for correct
  // usage. The behaviour was always right; the schema disagreed with it.
  mobileBreakpoint: z.number().int().nonnegative().optional(),
  mobileDrawerLabel: z.string().optional()
});
var SidebarTriggerSchema = z.object({
  sidebarId: z.string().min(1).optional(),
  children: z.unknown().optional(),
  className: z.string().optional(),
  tooltip: z.string().optional(),
  ariaLabel: z.string().optional(),
  testId: z.string().optional(),
  onClick: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function"
  }).optional()
});
var SidebarContentSchema = z.object({
  children: z.unknown().optional(),
  className: z.string().optional(),
  testId: z.string().optional(),
  /**
   * Fragment id the "Skip to content" link targets, or `false` to opt out
   * of the skip link entirely. Defaults to `'main'`.
   */
  skipLinkTarget: z.union([z.string(), z.literal(false)]).optional()
});
var SidebarLayoutSchema = z.object({
  sidebar: z.unknown(),
  children: z.unknown().optional(),
  className: z.string().optional(),
  side: z.enum(["start", "end"]).optional(),
  testId: z.string().optional()
}).refine((v) => Object.prototype.hasOwnProperty.call(v, "sidebar"), {
  message: "`sidebar` is required",
  path: ["sidebar"]
});
var optionalFn = z.unknown().refine((v) => v === void 0 || typeof v === "function", {
  message: "expected function"
}).optional();
var SidebarItemPropsSchema = z.object({
  label: z.string().min(1),
  icon: z.unknown().optional(),
  badge: z.unknown().optional(),
  chevron: z.boolean().optional(),
  href: z.string().min(1).optional(),
  onClick: optionalFn,
  active: z.boolean().optional(),
  disabled: z.boolean().optional(),
  className: z.string().optional(),
  testId: z.string().optional(),
  asChild: z.boolean().optional(),
  // `as` accepts an intrinsic tag name OR a component (function / object
  // — `memo` and `forwardRef` results are objects, not functions).
  as: z.unknown().refine(
    (v) => v === void 0 || typeof v === "string" || typeof v === "function" || typeof v === "object" && v !== null,
    { message: "expected an element type (tag name or component)" }
  ).optional(),
  children: z.unknown().optional()
}).passthrough();
var SidebarItemSchema = SidebarItemPropsSchema;
var SidebarGroupPropsSchema = z.object({
  label: z.string().min(1),
  icon: z.unknown().optional(),
  defaultOpen: z.boolean().optional(),
  open: z.boolean().optional(),
  onOpenChange: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function"
  }).optional(),
  className: z.string().optional(),
  testId: z.string().optional(),
  children: z.unknown().optional()
});
var SidebarGroupSchema = SidebarGroupPropsSchema;
var SidebarSectionHeaderPropsSchema = z.object({
  label: z.string().min(1),
  className: z.string().optional(),
  testId: z.string().optional()
});
var SidebarSectionHeaderSchema = SidebarSectionHeaderPropsSchema;
var SidebarSeparatorPropsSchema = z.object({
  className: z.string().optional(),
  testId: z.string().optional()
});
var SidebarSeparatorSchema = SidebarSeparatorPropsSchema;

// src/state-classes.ts
var SIDEBAR_STATE_CLASSES = [
  "sidebar--collapsed",
  "sidebar--side-start",
  "sidebar--side-end",
  "sidebar--collapsible-none",
  "sidebar--collapsible-icon",
  "sidebar--collapsible-full",
  "sidebar--mobile",
  "sidebar__content--collapsed",
  "sidebar__content--mobile",
  "sidebar__handle--dragging",
  "sidebar__trigger--collapsed",
  "sidebar__skip-link--collapsed",
  "sidebar__item--active",
  "sidebar__item--disabled",
  "sidebar__item--collapsed",
  "sidebar__group--open",
  "sidebar__group--collapsed",
  "sidebar__group__chevron--open",
  "sidebar-layout--side-start",
  "sidebar-layout--side-end"
];
var SIDEBAR_ELEMENT_CLASSES = [
  "sidebar",
  "sidebar__painter",
  "sidebar__body",
  "sidebar__content",
  "sidebar__handle",
  "sidebar__trigger",
  "sidebar__skip-link",
  "sidebar__separator",
  "sidebar__section-header",
  "sidebar__item",
  "sidebar__item__icon",
  "sidebar__item__label",
  "sidebar__item__badge",
  "sidebar__item__chevron",
  "sidebar__group",
  "sidebar__group__header",
  "sidebar__group__icon",
  "sidebar__group__label",
  "sidebar__group__chevron",
  "sidebar__group__body",
  "sidebar-layout",
  "sidebar-layout__main"
];
var SIDEBAR_REQUIRED_SELECTORS = [
  ".sidebar__item:hover",
  ".sidebar__item:focus-visible",
  ".sidebar__group__header:hover",
  ".sidebar__group__header:focus-visible",
  ".sidebar__trigger:hover",
  ".sidebar__trigger:focus-visible",
  ".sidebar__skip-link:hover",
  ".sidebar__skip-link:focus-visible",
  ".sidebar__handle:hover",
  ".sidebar__handle:focus-visible"
];
var SIDEBAR_THEME_TOKENS = [
  "--sidebar__surface",
  "--sidebar__fg",
  "--sidebar__fg-dim",
  "--sidebar__border",
  "--sidebar__focus-ring",
  "--sidebar__item-radius",
  "--sidebar__item-bg-hover",
  "--sidebar__item-bg-active",
  "--sidebar__item-fg-active",
  "--sidebar__item-marker-active",
  "--sidebar__item-fg-disabled"
];
var SIDEBAR_PAINT_CHANNELS = [
  // Shell channels — forwarded by <Sidebar>'s <aside>, consumed by the
  // non-stamped painter child (pre-existing, iter30).
  "--__sb-bg",
  "--__sb-fg",
  "--__sb-border",
  // Row channels — the interactive surfaces (item, group header, trigger,
  // skip-link) paint through these, so a stylesheet rule can retune them.
  "--__sb-row-bg",
  "--__sb-row-fg",
  // Resize-handle hairline, so hover / drag get a real affordance.
  "--__sb-handle-line"
];
var UseSidebarOptionsSchema = z.object({
  defaultCollapsed: z.boolean().optional(),
  defaultWidth: z.number().int().min(SIDEBAR_MIN_WIDTH).max(SIDEBAR_MAX_WIDTH).optional(),
  collapsed: z.boolean().optional(),
  width: z.number().int().min(SIDEBAR_MIN_WIDTH).max(SIDEBAR_MAX_WIDTH).optional(),
  onCollapsedChange: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function"
  }).optional(),
  onWidthChange: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function"
  }).optional(),
  storageKey: z.string().min(1).optional(),
  persistKey: z.string().min(1).optional(),
  hotkey: z.string().min(1).optional(),
  side: z.enum(["start", "end"]).optional()
});
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-sidebar";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function collectStyledClasses(doc) {
  const styled = /* @__PURE__ */ new Set();
  const CLASS_TOKEN = /\.(-?[_a-zA-Z][\w-]*)/g;
  const walk = (rules) => {
    if (!rules) return;
    for (let i = 0; i < rules.length; i += 1) {
      const rule = rules[i];
      if (typeof rule?.selectorText === "string") {
        for (const m of rule.selectorText.matchAll(CLASS_TOKEN)) {
          if (m[1]) styled.add(m[1]);
        }
      }
      if (rule?.cssRules) walk(rule.cssRules);
    }
  };
  const sheets = doc.styleSheets;
  for (let i = 0; i < sheets.length; i += 1) {
    try {
      walk(sheets[i]?.cssRules);
    } catch {
    }
  }
  return styled;
}
function now() {
  return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}
function selfCheck(root, opts) {
  const start = now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const allStamped = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-sidebar"]')
    );
    const instances = allStamped.filter((el) => {
      const role = el.getAttribute("role");
      const tag = el.tagName.toLowerCase();
      return tag === "aside" || role === "complementary";
    });
    if (instances.length === 0) return findings;
    for (const inst of instances) {
      const skipEls = Array.from(
        inst.querySelectorAll("a, button, [data-skip-link]")
      );
      const hasSkip = skipEls.some((el) => {
        const text = (el.textContent ?? "").trim().toLowerCase();
        const aria = (el.getAttribute("aria-label") ?? "").trim().toLowerCase();
        return /^skip/.test(text) || /^skip/.test(aria) || el.hasAttribute("data-skip-link");
      });
      if (!hasSkip) {
        findings.push({
          libId: LIB_ID,
          code: "SB_NO_SKIP_LINK",
          category: "a11y",
          severity: "minor",
          message: 'Sidebar lacks a visible "Skip to content" affordance. Keyboard users have no fast path past nav.',
          selector: selectorFor(inst),
          suggestedFix: 'Add a `<a data-skip-link href="#main">Skip to content</a>` as the first focusable element in the sidebar.'
        });
      }
    }
    const doc = instances[0]?.ownerDocument;
    if (doc) {
      const styled = collectStyledClasses(doc);
      const present = /* @__PURE__ */ new Set();
      for (const inst of instances) {
        const scope = [inst, ...Array.from(inst.querySelectorAll("*"))];
        for (const el of scope) {
          for (const cls of Array.from(el.classList)) {
            if (SIDEBAR_STATE_CLASSES.includes(cls)) {
              present.add(cls);
            }
          }
        }
      }
      const inert = Array.from(present).filter((cls) => !styled.has(cls));
      const stylesheetLoaded = styled.has("sidebar__item");
      if (inert.length > 0 && !stylesheetLoaded) {
        findings.push({
          libId: LIB_ID,
          code: "SB_STATE_CLASS_INERT",
          category: "stylesheet",
          severity: "major",
          message: `@norkron/web-sidebar's stylesheet is not loaded, so ${inert.length} state class(es) render inert \u2014 including ${inert.includes("sidebar__item--active") ? "`sidebar__item--active`, so the current page is not visually indicated" : `\`${inert[0]}\``}.`,
          selector: selectorFor(instances[0]),
          suggestedFix: "Add `import '@norkron/web-sidebar/styles.css'` once at your app entry, or write rules for these classes in your own stylesheet.",
          extras: { inertClasses: inert }
        });
      } else {
        for (const cls of inert) {
          findings.push({
            libId: LIB_ID,
            code: "SB_STATE_CLASS_INERT",
            category: "stylesheet",
            severity: "major",
            message: `State class \`${cls}\` is emitted into the DOM but no loaded stylesheet has a rule for it \u2014 the state is invisible to sighted users.`,
            selector: `.${cls}`,
            suggestedFix: `Add a rule for \`.${cls}\` (the lib ships one in @norkron/web-sidebar/styles.css), or stop emitting the class.`
          });
        }
      }
      if (stylesheetLoaded) {
        for (const token of SIDEBAR_THEME_TOKENS) {
          if (getCssVar(token, "") !== "") continue;
          findings.push({
            libId: LIB_ID,
            code: "SB_TOKEN_UNDEFINED",
            category: "theme",
            severity: "major",
            message: `Theme token \`${token}\` is undefined, so @norkron/web-sidebar's stylesheet falls back to a hardcoded value that ignores the active theme.`,
            selector: ":root",
            suggestedFix: "Ship @norkron/web-themer's `base.css` unmodified (it declares every `--sidebar__*` token in both the light and dark blocks), or declare this token in your own theme.",
            extras: { token }
          });
        }
      }
    }
    void selectorFor;
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "SB_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  if (now() - start > safeOpts.budgetMs) {
    findings.push({
      libId: LIB_ID,
      code: "SB_SELFCHECK_BUDGET_EXCEEDED",
      category: "perf",
      severity: "info",
      message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms.`,
      extras: { budgetMs: safeOpts.budgetMs, elapsed: now() - start }
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.5.0";
var SidebarContext = createContext(null);
function useSidebarContext() {
  return useContext(SidebarContext);
}
function clamp(n) {
  if (n < SIDEBAR_MIN_WIDTH) return SIDEBAR_MIN_WIDTH;
  if (n > SIDEBAR_MAX_WIDTH) return SIDEBAR_MAX_WIDTH;
  return Math.round(n);
}
function isBrowser() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}
function readPersisted(key) {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    const candidate = parsed && typeof parsed === "object" && "v" in parsed ? parsed.v : parsed;
    if (typeof candidate !== "object" || candidate === null || typeof candidate.collapsed !== "boolean" || typeof candidate.width !== "number") {
      warn({
        fn: "readPersisted",
        code: "WEB_SIDEBAR_INVALID_WIDTH",
        message: `Persisted payload at '${key}' has wrong shape`
      });
      return null;
    }
    const { collapsed, width } = candidate;
    const clamped = clamp(width);
    if (clamped !== width) {
      warn({
        fn: "readPersisted",
        code: "WEB_SIDEBAR_INVALID_WIDTH",
        message: `Persisted width ${width}px clamped to ${clamped}px`
      });
    }
    return { collapsed, width: clamped };
  } catch {
    warn({
      fn: "readPersisted",
      code: "WEB_SIDEBAR_INVALID_WIDTH",
      message: `Failed to JSON.parse persisted payload at '${key}'`
    });
    return null;
  }
}
function persistKeyHandle(key) {
  return jsonStorage(key, {
    driver: "local",
    schema: (v) => {
      if (typeof v !== "object" || v === null || typeof v.collapsed !== "boolean" || typeof v.width !== "number") {
        throw new Error("invalid");
      }
      const { collapsed, width } = v;
      return { collapsed, width: clamp(width) };
    }
  });
}
function writePersisted(key, state) {
  if (!isBrowser()) return;
  try {
    const payload = {
      collapsed: state.collapsed,
      width: clamp(state.width)
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
  }
}

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}

// src/validate-input.ts
function reportIfBad(parsed, fn) {
  if (parsed.success) return;
  reportInvalidInput({
    lib: WEB_SIDEBAR_LIB,
    libToken: WEB_SIDEBAR_TOKEN,
    fn,
    parseResult: toZodLikeSafeParseError(
      parsed
    )
  });
}
function validateSidebarProps(props) {
  reportIfBad(SidebarPropsSchema.safeParse(props), "Sidebar");
}
function validateSidebarTriggerProps(props) {
  reportIfBad(SidebarTriggerSchema.safeParse(props), "SidebarTrigger");
}
function validateSidebarContentProps(props) {
  reportIfBad(SidebarContentSchema.safeParse(props), "SidebarContent");
}
function validateSidebarLayoutProps(props) {
  reportIfBad(SidebarLayoutSchema.safeParse(props), "SidebarLayout");
}
function validateUseSidebarOptions(opts) {
  reportIfBad(UseSidebarOptionsSchema.safeParse(opts), "useSidebar");
}
function validateSidebarItemProps(props) {
  reportIfBad(SidebarItemSchema.safeParse(props), "SidebarItem");
}
function validateSidebarGroupProps(props) {
  reportIfBad(SidebarGroupSchema.safeParse(props), "SidebarGroup");
}
function validateSidebarSectionHeaderProps(props) {
  reportIfBad(SidebarSectionHeaderSchema.safeParse(props), "SidebarSectionHeader");
}
function validateSidebarSeparatorProps(props) {
  reportIfBad(SidebarSeparatorSchema.safeParse(props), "SidebarSeparator");
}

// src/useSidebar.ts
function clampWidth(n, fn = "clampWidth") {
  if (Number.isNaN(n)) return SIDEBAR_DEFAULT_WIDTH;
  if (n < SIDEBAR_MIN_WIDTH) {
    warn({
      fn,
      code: "WEB_SIDEBAR_INVALID_WIDTH",
      message: `setWidth(${n}) clamped to ${SIDEBAR_MIN_WIDTH} (allowed [${SIDEBAR_MIN_WIDTH}, ${SIDEBAR_MAX_WIDTH}])`,
      severity: "warning",
      dedupKey: `${WEB_SIDEBAR_LIB}:${fn}:WEB_SIDEBAR_INVALID_WIDTH:under:${n}`
    });
    return SIDEBAR_MIN_WIDTH;
  }
  if (n > SIDEBAR_MAX_WIDTH) {
    warn({
      fn,
      code: "WEB_SIDEBAR_INVALID_WIDTH",
      message: `setWidth(${n}) clamped to ${SIDEBAR_MAX_WIDTH} (allowed [${SIDEBAR_MIN_WIDTH}, ${SIDEBAR_MAX_WIDTH}])`,
      severity: "warning",
      dedupKey: `${WEB_SIDEBAR_LIB}:${fn}:WEB_SIDEBAR_INVALID_WIDTH:over:${n}`
    });
    return SIDEBAR_MAX_WIDTH;
  }
  return Math.round(n);
}
function useSidebar(options = {}) {
  validateUseSidebarOptions(options);
  const {
    defaultCollapsed = false,
    defaultWidth = SIDEBAR_DEFAULT_WIDTH,
    collapsed: controlledCollapsed,
    width: controlledWidth,
    onCollapsedChange,
    onWidthChange,
    storageKey: storageKeyRaw,
    persistKey,
    hotkey = "b",
    side = "start"
  } = options;
  const storageKey = persistKey ?? storageKeyRaw;
  const reactId = useId();
  const id = useMemo(() => `web-sidebar-${reactId.replace(/[^a-zA-Z0-9-]/g, "")}`, [reactId]);
  const persistedRef = useRef(null);
  if (persistedRef.current === null && storageKey) {
    if (persistKey) {
      const v = persistKeyHandle(storageKey).get();
      persistedRef.current = v ?? null;
    } else {
      persistedRef.current = readPersisted(storageKey);
    }
  }
  const initialCollapsed = persistedRef.current?.collapsed ?? defaultCollapsed;
  const initialWidth = clampWidth(persistedRef.current?.width ?? defaultWidth);
  const disclosure = useDisclosure({
    defaultOpen: !initialCollapsed,
    onChange: (open) => {
      if (storageKey) {
        if (persistKey) {
          persistKeyHandle(storageKey).set({ collapsed: !open, width: widthRef.current });
        } else {
          writePersisted(storageKey, { collapsed: !open, width: widthRef.current });
        }
      }
      onCollapsedChange?.(!open);
    }
  });
  const isCollapsedControlled = typeof controlledCollapsed === "boolean";
  const collapsedFromDisclosure = !disclosure.open;
  const collapsed = isCollapsedControlled ? controlledCollapsed : collapsedFromDisclosure;
  const [uncontrolledWidth, setUncontrolledWidth] = useState(initialWidth);
  const isWidthControlled = typeof controlledWidth === "number";
  const width = isWidthControlled ? clampWidth(controlledWidth) : uncontrolledWidth;
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);
  const toggle = useCallback(() => {
    if (isCollapsedControlled) {
      onCollapsedChange?.(!controlledCollapsed);
      return;
    }
    disclosure.toggle();
  }, [isCollapsedControlled, controlledCollapsed, onCollapsedChange, disclosure]);
  const collapse = useCallback(() => {
    if (isCollapsedControlled) {
      onCollapsedChange?.(true);
      return;
    }
    disclosure.hide();
  }, [isCollapsedControlled, onCollapsedChange, disclosure]);
  const expand = useCallback(() => {
    if (isCollapsedControlled) {
      onCollapsedChange?.(false);
      return;
    }
    disclosure.show();
  }, [isCollapsedControlled, onCollapsedChange, disclosure]);
  const setWidth = useCallback(
    (px) => {
      const next = clampWidth(px, "setWidth");
      if (isWidthControlled) {
        onWidthChange?.(next);
        return;
      }
      setUncontrolledWidth((prev) => {
        if (prev === next) return prev;
        if (storageKey) {
          if (persistKey) {
            persistKeyHandle(storageKey).set({ collapsed, width: next });
          } else {
            writePersisted(storageKey, { collapsed, width: next });
          }
        }
        onWidthChange?.(next);
        return next;
      });
    },
    [isWidthControlled, onWidthChange, storageKey, persistKey, collapsed]
  );
  const nudgeWidth = useCallback(
    (delta) => {
      setWidth(widthRef.current + delta);
    },
    [setWidth]
  );
  useEffect(() => {
    if (!hotkey) return;
    if (typeof window === "undefined") return;
    const target = window;
    function onKeyDown(e) {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.altKey || e.shiftKey) return;
      if (e.key.toLowerCase() !== hotkey.toLowerCase()) return;
      e.preventDefault();
      toggle();
    }
    target.addEventListener("keydown", onKeyDown);
    return () => target.removeEventListener("keydown", onKeyDown);
  }, [hotkey, toggle]);
  return useMemo(
    () => ({
      collapsed,
      open: !collapsed,
      toggle,
      collapse,
      expand,
      width,
      setWidth,
      nudgeWidth,
      id,
      side
    }),
    [collapsed, toggle, collapse, expand, width, setWidth, nudgeWidth, id, side]
  );
}
function classes(...parts) {
  return parts.filter(Boolean).join(" ");
}
var DEFAULT_MOBILE_BREAKPOINT = 768;
function resolveCollapsible(v) {
  if (v === void 0) return "icon";
  if (v === true) return "icon";
  if (v === false) return "none";
  return v;
}
function useIsMobile(breakpoint) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (breakpoint <= 0) {
      setIsMobile(false);
      return;
    }
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const update = () => setIsMobile(mql.matches);
    update();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", update);
      return () => mql.removeEventListener("change", update);
    }
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, [breakpoint]);
  return isMobile;
}
var Sidebar = memo(function Sidebar2(props) {
  validateSidebarProps(props);
  const {
    children,
    className,
    ariaLabel,
    side = "start",
    resizable = true,
    collapsible,
    testId,
    defaultWidth,
    defaultCollapsed,
    width: controlledWidth,
    collapsed: controlledCollapsed,
    onCollapsedChange,
    onWidthChange,
    storageKey,
    persistKey,
    hotkey,
    mobileBreakpoint = DEFAULT_MOBILE_BREAKPOINT,
    mobileDrawerLabel
  } = props;
  const useSidebarOpts = useMemo(
    () => ({
      defaultCollapsed,
      defaultWidth,
      collapsed: controlledCollapsed,
      width: controlledWidth,
      onCollapsedChange,
      onWidthChange,
      storageKey,
      persistKey,
      hotkey,
      side
    }),
    [
      defaultCollapsed,
      defaultWidth,
      controlledCollapsed,
      controlledWidth,
      onCollapsedChange,
      onWidthChange,
      storageKey,
      persistKey,
      hotkey,
      side
    ]
  );
  const api = useSidebar(useSidebarOpts);
  const mode = resolveCollapsible(collapsible);
  const isMobile = useIsMobile(mobileBreakpoint);
  return /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      fallback: (error, reset) => /* @__PURE__ */ jsx(DefaultErrorFallback, { error, reset, title: "Sidebar failed to render" }),
      children: /* @__PURE__ */ jsx(SidebarContext.Provider, { value: api, children: isMobile ? /* @__PURE__ */ jsx(
        SidebarMobileShell,
        {
          api,
          ariaLabel: mobileDrawerLabel ?? ariaLabel,
          side,
          className,
          testId,
          children
        }
      ) : /* @__PURE__ */ jsx(
        SidebarShell,
        {
          api,
          side,
          ariaLabel,
          resizable: resizable && mode !== "none",
          collapsibleMode: mode,
          className,
          testId,
          children
        }
      ) })
    }
  );
});
function SidebarShell(props) {
  const { api, side, ariaLabel, resizable, collapsibleMode, className, testId, children } = props;
  const collapsedRenderWidth = collapsibleMode === "full" ? 0 : SIDEBAR_COLLAPSED_WIDTH;
  const renderWidth = api.collapsed ? collapsedRenderWidth : api.width;
  const style = {
    width: renderWidth,
    minWidth: renderWidth,
    maxWidth: renderWidth,
    position: "relative",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    // Custom-property forwards — opted out of paint-override detection
    // per the rule's `isCustomProperty` allowlist; the painter child
    // below consumes them.
    /* iter38 STR_THEME_VARIANT_MISSING — anchor to canonical web-themer
     * tokens that DO declare light + dark variants in base.css
     * (`--norkron-text-primary`, `--norkron-surface-elevated`,
     * `--norkron-border-default`). The previous `--color-*` brand
     * aliases were undefined in the editor cascade and fell through to
     * `currentColor` / `transparent`, pinning the painter's resolved
     * `color` at `rgb(0,0,0)` across both themes. Legacy brand aliases
     * stay as the first fallback hop so consumers that DO author them
     * keep their override; themer tokens are the second hop so the
     * audit env always resolves a flipping value. */
    ["--__sb-fg"]: "var(--color-fg-default, var(--norkron-text-primary, #1f2937))",
    ["--__sb-bg"]: "var(--color-bg-subtle, var(--norkron-surface-elevated, #ffffff))",
    ["--__sb-border"]: "var(--color-border-default, var(--norkron-border-default, #cbd5e1))"
  };
  const handle = resizable && !api.collapsed ? /* @__PURE__ */ jsx(SidebarResizeHandle, { api, side }) : null;
  return /* @__PURE__ */ jsxs(
    "aside",
    {
      id: api.id,
      role: "complementary",
      "aria-label": ariaLabel,
      "data-testid": testId ?? "sidebar",
      "data-keep-clear": "true",
      ...withConformance("@norkron/web-sidebar", LIB_VERSION),
      className: classes(
        "sidebar",
        api.collapsed && "sidebar--collapsed",
        `sidebar--side-${side}`,
        `sidebar--collapsible-${collapsibleMode}`,
        className
      ),
      style,
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            "aria-hidden": true,
            className: "sidebar__painter",
            style: {
              position: "absolute",
              inset: 0,
              color: "var(--__sb-fg)",
              background: "var(--__sb-bg)",
              borderInlineColor: "var(--__sb-border)",
              pointerEvents: "none",
              zIndex: 0
            }
          }
        ),
        side === "end" ? handle : null,
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "sidebar__body",
            style: {
              flex: "1 1 auto",
              minWidth: 0,
              position: "relative",
              zIndex: 1,
              color: "var(--__sb-fg)"
            },
            children
          }
        ),
        side === "start" ? handle : null
      ]
    }
  );
}
function SidebarMobileShell(props) {
  const { api, side, ariaLabel, className, testId, children } = props;
  const drawerSide = side === "start" ? "left" : "right";
  const onOpenChange = useCallback(
    (open) => {
      if (open) api.expand();
      else api.collapse();
    },
    [api]
  );
  return /* @__PURE__ */ jsx(Drawer, { side: drawerSide, open: api.open, onOpenChange, modal: true, children: /* @__PURE__ */ jsx(
    DrawerContent,
    {
      ariaLabel: ariaLabel ?? "Sidebar",
      className: classes(
        "sidebar",
        "sidebar--mobile",
        `sidebar--side-${side}`,
        className
      ),
      children: /* @__PURE__ */ jsx(DrawerBody, { children: /* @__PURE__ */ jsx(
        "div",
        {
          id: api.id,
          "data-testid": testId ?? "sidebar",
          "data-keep-clear": "true",
          ...withConformance("@norkron/web-sidebar", LIB_VERSION),
          className: "sidebar__content sidebar__content--mobile",
          children
        }
      ) })
    }
  ) });
}
function SidebarResizeHandle(props) {
  const { api, side } = props;
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(api.width);
  const [dragging, setDragging] = useState(false);
  const onPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      draggingRef.current = true;
      setDragging(true);
      startXRef.current = e.clientX;
      startWidthRef.current = api.width;
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [api.width]
  );
  const onPointerMove = useCallback(
    (e) => {
      if (!draggingRef.current) return;
      const delta = e.clientX - startXRef.current;
      const direction = side === "start" ? 1 : -1;
      api.setWidth(startWidthRef.current + delta * direction);
    },
    [api, side]
  );
  const onPointerUp = useCallback((e) => {
    draggingRef.current = false;
    setDragging(false);
    try {
      ;
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
    }
  }, []);
  const onKeyDown = useCallback(
    (e) => {
      const step = SIDEBAR_KEYBOARD_STEP;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        api.nudgeWidth(side === "start" ? -step : step);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        api.nudgeWidth(side === "start" ? step : -step);
      } else if (e.key === "Home") {
        e.preventDefault();
        api.setWidth(SIDEBAR_MIN_WIDTH);
      } else if (e.key === "End") {
        e.preventDefault();
        api.setWidth(SIDEBAR_MAX_WIDTH);
      }
    },
    [api, side]
  );
  const handleStyle = {
    flex: "0 0 var(--norkron-touch-target, 2.75rem)",
    minWidth: "var(--norkron-touch-target, 2.75rem)",
    minHeight: "var(--norkron-touch-target, 2.75rem)",
    alignSelf: "stretch",
    boxShadow: `var(--__sb-handle-line, inset ${side === "start" ? "-" : ""}var(--norkron-space-1, 8px) 0 0 -6px var(--color-border-default, #d0d7de))`,
    cursor: "col-resize",
    touchAction: "none",
    boxSizing: "border-box"
  };
  const handle = /* @__PURE__ */ jsx(
    "div",
    {
      role: "separator",
      "aria-orientation": "vertical",
      "aria-label": "Resize sidebar",
      "aria-valuemin": SIDEBAR_MIN_WIDTH,
      "aria-valuemax": SIDEBAR_MAX_WIDTH,
      "aria-valuenow": api.width,
      tabIndex: 0,
      className: classes("sidebar__handle", dragging && "sidebar__handle--dragging"),
      "data-testid": "sidebar__handle",
      style: handleStyle,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown
    }
  );
  return /* @__PURE__ */ jsx(RichTooltip, { trigger: handle, content: `Resize sidebar (current ${api.width}px)` });
}
var SidebarTrigger = memo(function SidebarTrigger2(props) {
  validateSidebarTriggerProps(props);
  const { sidebarId, children, className, tooltip, ariaLabel, testId, onClick } = props;
  const triggerLabel = ariaLabel ?? tooltip ?? "Toggle sidebar";
  const ctx = useSidebarContext();
  useFocusRestore(!!ctx && ctx.collapsed);
  const resolvedTargetId = sidebarId ?? ctx?.id;
  const expanded = ctx ? !ctx.collapsed : true;
  const handleClick = useCallback(
    (e) => {
      ctx?.toggle();
      onClick?.(e);
    },
    [ctx, onClick]
  );
  const triggerStyle = {
    minWidth: "var(--norkron-touch-target, 2.75rem)",
    minHeight: "var(--norkron-touch-target, 2.75rem)",
    padding: "var(--norkron-space-1, 8px) var(--norkron-space-2, 12px)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--norkron-space-1, 8px)",
    // iter63 paint channel — `transparent` fallback keeps the trigger
    // byte-identical without the stylesheet; `.sidebar__trigger:hover`
    // sets the channel when it IS imported.
    background: "var(--__sb-row-bg, transparent)",
    border: "0 solid transparent",
    borderRadius: "var(--radius-md, 8px)",
    cursor: "pointer",
    boxSizing: "border-box"
  };
  const button = /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      "aria-expanded": expanded,
      "aria-controls": resolvedTargetId,
      "aria-label": triggerLabel,
      "data-testid": testId ?? "sidebar__trigger",
      className: classes("sidebar__trigger", !expanded && "sidebar__trigger--collapsed", className),
      onClick: handleClick,
      style: triggerStyle,
      "data-allow-single-theme": "true",
      children
    }
  );
  if (!tooltip) return button;
  return /* @__PURE__ */ jsx(RichTooltip, { trigger: button, content: tooltip });
});
function SidebarSkipLink(props) {
  const { target, collapsed } = props;
  const style = collapsed ? {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Full-width of the icon rail — no horizontal margin, so the
    // button's left edge stays on the 4px baseline grid (an
    // `auto` margin would land it on a 2px off-grid offset,
    // R1-A2). Height is the touch-target minimum (WCAG 2.5.5).
    width: "100%",
    height: "var(--norkron-touch-target, 2.75rem)",
    minHeight: "var(--norkron-touch-target, 2.75rem)",
    margin: "var(--norkron-space-1, 8px) 0 var(--norkron-space-0, 4px)",
    boxSizing: "border-box",
    /* iter28: route through `--norkron-text-primary` (dark in
     * light theme, light in dark theme — guaranteed
     * theme-flipping per web-themer base.css) so the skip-link
     * bg responds to `[data-theme="dark"]`. The previous
     * `--color-bg-emphasis` brand alias had no dark variant
     * in the editor cascade, leaving STR_THEME_VARIANT_MISSING
     * firing on every sidebar example. The inverted-emphasis
     * (dark-on-light / light-on-dark) is the canonical
     * "skip link" look across both themes; consumers can
     * still override via `--color-bg-emphasis` if both theme
     * variants are explicitly authored. */
    background: "var(--color-bg-emphasis, var(--norkron-text-primary, #1f2937))",
    color: "var(--color-fg-on-emphasis, var(--norkron-surface-base, #ffffff))",
    /* iter38 STR_THEME_VARIANT_MISSING — drop the `border:`
     * shorthand reset. The matcher treats any inline `border:`
     * occurrence as the consumer claiming ownership of all four
     * border-*-color computed props; with `0 solid transparent`
     * the resolved color is `rgba(0,0,0,0)` and does NOT flip
     * across themes, firing the rule on every example. Using
     * the long-form `borderWidth: 0` plus `borderStyle: 'none'`
     * achieves the same visual reset without touching any
     * border-*-color, so the matcher never adds those props to
     * ownedKeys. */
    borderWidth: 0,
    borderStyle: "none",
    borderRadius: "var(--radius-md, 8px)",
    fontSize: "var(--norkron-font-size-sm, 13px)",
    cursor: "pointer"
  } : {
    display: "flex",
    alignItems: "center",
    // Width pinned to the content box so the button never
    // overflows the `overflow-x:hidden` scroll region (R1-A3)
    // and its text wraps inside the column instead of
    // demanding more width (R6-A3).
    width: "auto",
    maxWidth: "100%",
    minHeight: "var(--norkron-touch-target, 2.75rem)",
    padding: "var(--norkron-space-1, 8px) var(--norkron-space-2, 12px)",
    margin: "var(--norkron-space-1, 8px) var(--norkron-space-1, 8px) var(--norkron-space-0, 4px)",
    boxSizing: "border-box",
    /* iter28: route through `--norkron-text-primary` (dark in
     * light theme, light in dark theme — guaranteed
     * theme-flipping per web-themer base.css) so the skip-link
     * bg responds to `[data-theme="dark"]`. The previous
     * `--color-bg-emphasis` brand alias had no dark variant
     * in the editor cascade, leaving STR_THEME_VARIANT_MISSING
     * firing on every sidebar example. The inverted-emphasis
     * (dark-on-light / light-on-dark) is the canonical
     * "skip link" look across both themes; consumers can
     * still override via `--color-bg-emphasis` if both theme
     * variants are explicitly authored. */
    background: "var(--color-bg-emphasis, var(--norkron-text-primary, #1f2937))",
    color: "var(--color-fg-on-emphasis, var(--norkron-surface-base, #ffffff))",
    /* iter38 STR_THEME_VARIANT_MISSING — see expanded-form
     * comment above; drop `border:` shorthand to avoid the
     * matcher claiming border-*-color ownership for the
     * `rgba(0,0,0,0)` reset value. */
    borderWidth: 0,
    borderStyle: "none",
    borderRadius: "var(--radius-md, 8px)",
    fontSize: "var(--norkron-font-size-xs, 11px)",
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "start",
    whiteSpace: "normal",
    overflowWrap: "anywhere"
  };
  const onClick = useCallback(() => {
    if (typeof document === "undefined") return;
    const main = target && document.getElementById(target) || document.querySelector("main") || document.querySelector('[role="main"]');
    if (!main) return;
    if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
    main.focus();
    main.scrollIntoView({ block: "start" });
  }, [target]);
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      "data-skip-link": true,
      "data-testid": "sidebar__skip-link",
      className: classes("sidebar__skip-link", collapsed && "sidebar__skip-link--collapsed"),
      style,
      onClick,
      "aria-label": collapsed ? "Skip to content" : void 0,
      title: collapsed ? "Skip to content" : void 0,
      children: collapsed ? /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: "\u2913" }) : "Skip to content"
    }
  );
}
var SidebarContent = memo(function SidebarContent2(props) {
  validateSidebarContentProps(props);
  const { children, className, testId, skipLinkTarget } = props;
  const ctx = useSidebarContext();
  const isEmpty = children == null || Array.isArray(children) && children.length === 0;
  const collapsed = ctx?.collapsed ?? false;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: classes(
        "sidebar__content",
        collapsed && "sidebar__content--collapsed",
        className
      ),
      "data-testid": testId ?? "sidebar__content",
      style: { overflowX: "hidden", overflowY: "auto", height: "100%", minWidth: 0, position: "relative" },
      ...withConformance("@norkron/web-sidebar", LIB_VERSION),
      children: [
        skipLinkTarget !== false ? /* @__PURE__ */ jsx(SidebarSkipLink, { target: skipLinkTarget || void 0, collapsed }) : null,
        isEmpty ? /* @__PURE__ */ jsx(EmptyState, { title: "Empty sidebar", description: "No content was passed." }) : children
      ]
    }
  );
});
var SidebarLayout = memo(function SidebarLayout2(props) {
  validateSidebarLayoutProps(props);
  const { sidebar, children, className, side = "start", testId } = props;
  const style = {
    display: "grid",
    gridTemplateColumns: side === "start" ? "auto 1fr" : "1fr auto",
    minHeight: "100%"
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: classes("sidebar-layout", `sidebar-layout--side-${side}`, className),
      "data-testid": testId ?? "sidebar-layout",
      style,
      children: [
        side === "start" ? sidebar : null,
        /* @__PURE__ */ jsx("main", { className: "sidebar-layout__main", "data-testid": "sidebar-layout__main", children }),
        side === "end" ? sidebar : null
      ]
    }
  );
});
function useSidebarApi() {
  const ctx = useSidebarContext();
  if (!ctx) {
    throw new Error(
      "[@norkron/web-sidebar] useSidebarApi() called outside <Sidebar>."
    );
  }
  return ctx;
}
var SidebarSeparator = memo(function SidebarSeparator2(props) {
  validateSidebarSeparatorProps(props);
  const { className, testId } = props;
  const style = {
    border: "0 solid transparent",
    boxShadow: "inset 0 1px 0 var(--__sb-border, var(--color-border-default, currentColor))",
    height: "var(--norkron-space-0, 4px)",
    background: "transparent",
    margin: "var(--norkron-space-1, 8px) 0"
  };
  return /* @__PURE__ */ jsx(
    "hr",
    {
      role: "separator",
      "data-testid": testId ?? "sidebar__separator",
      className: classes("sidebar__separator", className),
      style
    }
  );
});
var SidebarSectionHeader = memo(function SidebarSectionHeader2(props) {
  validateSidebarSectionHeaderProps(props);
  const { label, className, testId } = props;
  const ctx = useSidebarContext();
  if (ctx?.collapsed) return null;
  const style = {
    fontSize: "var(--norkron-font-size-xs, 11px)",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    opacity: 0.7,
    padding: "var(--norkron-space-1, 8px) var(--norkron-space-2, 12px) var(--norkron-space-0, 4px)"
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "heading",
      "aria-level": 3,
      "data-testid": testId ?? "sidebar__section-header",
      className: classes("sidebar__section-header", className),
      style,
      children: label
    }
  );
});
function SidebarItemImpl(props) {
  validateSidebarItemProps(props);
  const {
    label,
    icon,
    badge,
    chevron,
    href,
    onClick,
    active,
    disabled,
    className,
    testId,
    as,
    asChild,
    children,
    ...rest
  } = props;
  const ctx = useSidebarContext();
  const collapsed = ctx?.collapsed ?? false;
  const inner = /* @__PURE__ */ jsxs(Fragment, { children: [
    icon ? /* @__PURE__ */ jsx("span", { className: "sidebar__item__icon", "aria-hidden": true, children: icon }) : null,
    !collapsed ? /* @__PURE__ */ jsx("span", { className: "sidebar__item__label", children: label }) : null,
    !collapsed && badge != null ? /* @__PURE__ */ jsx("span", { className: "sidebar__item__badge", children: badge }) : null,
    !collapsed && chevron ? /* @__PURE__ */ jsx("span", { className: "sidebar__item__chevron", "aria-hidden": true, children: "\u203A" }) : null
  ] });
  const cls = classes(
    "sidebar__item",
    active && "sidebar__item--active",
    disabled && "sidebar__item--disabled",
    collapsed && "sidebar__item--collapsed",
    className
  );
  const style = {
    display: "flex",
    alignItems: "center",
    gap: "var(--norkron-space-1, 8px)",
    padding: "var(--norkron-space-1, 8px) var(--norkron-space-2, 12px)",
    minHeight: "var(--norkron-touch-target, 2.75rem)",
    boxSizing: "border-box",
    // iter30: route through `var(--color-fg-default)` so the cascade
    // flips on DOM-only theme toggles (the STR_THEME_VARIANT_MISSING
    // audit path). Previously baked the resolved hex from useThemeVar.
    // iter63: `--__sb-row-fg` / `--__sb-row-bg` are the paint channels
    // `sidebar.css` sets per state — the previous hardcoded
    // `background: 'transparent'` is what made the state classes inert.
    // Both fallbacks are the exact v0.3.x values.
    color: "var(--__sb-row-fg, var(--color-fg-default, currentColor))",
    textDecoration: "none",
    background: "var(--__sb-row-bg, transparent)",
    border: "0 solid transparent",
    width: "100%",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1
  };
  const dt = testId ?? "sidebar__item";
  const ariaCurrent = active ? "page" : void 0;
  const handleClick = disabled ? (event) => event.preventDefault() : onClick;
  if (asChild && isValidElement(children)) {
    const childProps = children.props;
    const childOnClick = childProps.onClick;
    return cloneElement(children, {
      ...rest,
      className: classes(cls, childProps.className),
      style: { ...style, ...childProps.style ?? {} },
      "data-testid": childProps["data-testid"] ?? dt,
      "aria-current": ariaCurrent,
      "aria-disabled": disabled,
      title: collapsed ? label : childProps.title,
      onClick: (e) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        childOnClick?.(e);
        handleClick?.(e);
      },
      children: childProps.children ?? inner
    });
  }
  const Component = as ?? (href ? "a" : "button");
  const isIntrinsicButton = Component === "button";
  const elementProps = {
    ...rest,
    className: cls,
    style,
    "data-testid": dt,
    "aria-current": ariaCurrent,
    "aria-disabled": disabled,
    title: collapsed ? label : void 0,
    onClick: handleClick
  };
  if (isIntrinsicButton) {
    elementProps.type = rest.type ?? "button";
    elementProps.disabled = disabled;
  } else if (disabled) {
    elementProps.tabIndex = -1;
    elementProps.onClickCapture = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
  }
  if (href !== void 0 && !isIntrinsicButton) {
    elementProps.href = disabled ? void 0 : href;
  }
  return /* @__PURE__ */ jsx(Component, { ...elementProps, children: children ?? inner });
}
var SidebarItem = memo(SidebarItemImpl);
var SidebarGroup = memo(function SidebarGroup2(props) {
  validateSidebarGroupProps(props);
  const {
    label,
    icon,
    defaultOpen = true,
    open: controlledOpen,
    onOpenChange,
    className,
    testId,
    children
  } = props;
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const isControlled = typeof controlledOpen === "boolean";
  const open = isControlled ? controlledOpen : uncontrolled;
  const ctx = useSidebarContext();
  const sidebarCollapsed = ctx?.collapsed ?? false;
  const toggle = useCallback(() => {
    if (isControlled) {
      onOpenChange?.(!controlledOpen);
      return;
    }
    setUncontrolled((prev) => {
      const next = !prev;
      onOpenChange?.(next);
      return next;
    });
  }, [isControlled, controlledOpen, onOpenChange]);
  if (sidebarCollapsed) {
    return icon ? /* @__PURE__ */ jsx(
      "div",
      {
        className: classes("sidebar__group", "sidebar__group--collapsed", className),
        "data-testid": testId ?? "sidebar__group",
        title: label,
        children: /* @__PURE__ */ jsx("span", { className: "sidebar__group__icon", "aria-hidden": true, children: icon })
      }
    ) : null;
  }
  const bodyId = `sidebar-group-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: classes(
        "sidebar__group",
        open && "sidebar__group--open",
        className
      ),
      "data-testid": testId ?? "sidebar__group",
      children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            className: "sidebar__group__header",
            "data-testid": "sidebar__group__header",
            "aria-expanded": open,
            "aria-controls": bodyId,
            onClick: toggle,
            style: {
              // Tokenized through --norkron-space-* (R1-A2). Touch
              // target enforced via min-height so the group header
              // stays tappable in both sidebar modes (WCAG 2.5.5).
              display: "flex",
              alignItems: "center",
              gap: "var(--norkron-space-1, 8px)",
              width: "100%",
              padding: "var(--norkron-space-1, 8px) var(--norkron-space-2, 12px)",
              minHeight: "var(--norkron-touch-target, 2.75rem)",
              boxSizing: "border-box",
              // iter63 paint channel — see the module header. The
              // `transparent` fallback preserves v0.3.x rendering.
              background: "var(--__sb-row-bg, transparent)",
              border: "0 solid transparent",
              fontWeight: 600,
              cursor: "pointer"
            },
            children: [
              icon ? /* @__PURE__ */ jsx("span", { className: "sidebar__group__icon", "aria-hidden": true, children: icon }) : null,
              /* @__PURE__ */ jsx("span", { className: "sidebar__group__label", style: { flex: 1, textAlign: "start" }, children: label }),
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: classes(
                    "sidebar__group__chevron",
                    open && "sidebar__group__chevron--open"
                  ),
                  "aria-hidden": true,
                  style: {
                    transition: "transform 120ms",
                    transform: open ? "rotate(90deg)" : "rotate(0deg)"
                  },
                  children: "\u203A"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            id: bodyId,
            className: "sidebar__group__body",
            role: "region",
            "aria-label": label,
            hidden: !open,
            children
          }
        )
      ]
    }
  );
});
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-sidebar",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Below the `md` boundary (768px, the `mobileBreakpoint` default) the
    // aside is replaced by a modal drawer — Sidebar.tsx `useIsMobile` +
    // `SidebarMobileShell`. Hidden behind the trigger ⇒ 'collapsed'.
    xs: "collapsed",
    sm: "collapsed",
    // `md` and up: the full inline aside. Collapse from here on is
    // user-driven state, not a viewport tier.
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

// src/index.ts
registerLazy(
  "@norkron/web-sidebar",
  () => selfCheck
);

export { LIB_VERSION, RESPONSIVE_CONTRACT, SIDEBAR_ELEMENT_CLASSES, SIDEBAR_PAINT_CHANNELS, SIDEBAR_REQUIRED_SELECTORS, SIDEBAR_STATE_CLASSES, SIDEBAR_THEME_TOKENS, Sidebar, SidebarContent, SidebarGroup, SidebarItem, SidebarLayout, SidebarSectionHeader, SidebarSeparator, SidebarTrigger, selfCheck, useSidebar, useSidebarApi };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map